import { z } from 'zod';
import { CATEGORIES, SEVERITIES, SOURCE_IDS } from './categories.js';

export const categorySchema = z.enum(CATEGORIES);
export const severitySchema = z.enum(SEVERITIES);
export const sourceIdSchema = z.enum(SOURCE_IDS);

export const normalizedEventSchema = z.object({
  source: sourceIdSchema,
  sourceId: z.string().min(1),
  category: categorySchema,
  severity: severitySchema,
  severityScore: z.number().min(0).max(100),
  title: z.string().min(1),
  description: z.string().optional(),
  url: z.string().url().optional(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  radiusKm: z.number().positive().optional(),
  countryCode: z.string().length(2).optional(),
  occurredAt: z.number().int().positive(),
  rawJson: z.unknown(),
});

/** A CSV import row before geocoding. */
export const employeeImportRowSchema = z
  .object({
    external_id: z.string().optional(),
    name: z.string().optional(),
    email: z.string().email().optional().or(z.literal('')),
    role: z.string().optional(),
    location_label: z.string().optional(),
    address: z.string().optional(),
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
  })
  .refine((r) => r.name || r.external_id, {
    message: 'Each row needs a name or external_id',
  })
  .refine((r) => r.address || (r.lat != null && r.lng != null), {
    message: 'Each row needs an address or lat+lng',
  });

export const lookupRequestSchema = z
  .object({
    address: z.string().min(1).optional(),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    radiusKm: z.number().positive().max(2000).default(25),
  })
  .refine((r) => r.address || (r.lat != null && r.lng != null), {
    message: 'Provide an address or lat+lng',
  });

export const geocodeRequestSchema = z.object({
  query: z.string().min(1),
});

export const feedbackSchema = z.object({
  eventId: z.number().int().positive(),
  relevant: z.boolean(),
  note: z.string().max(2000).optional(),
});

export const eventsQuerySchema = z.object({
  since: z.coerce.number().int().optional(),
  until: z.coerce.number().int().optional(),
  category: categorySchema.optional(),
  severity: severitySchema.optional(),
  country: z.string().length(2).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

export type NormalizedEventInput = z.infer<typeof normalizedEventSchema>;
export type EmployeeImportRow = z.infer<typeof employeeImportRowSchema>;
export type LookupRequest = z.infer<typeof lookupRequestSchema>;
export type EventsQuery = z.infer<typeof eventsQuerySchema>;
